import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Link,
  TableSortLabel,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  Button,
  Tooltip,
  Chip,
  Stack
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { SectionCard, SectionHeader, SectionLoading, SentimentBadge } from './ui/Section';

// Define the Post type directly here or import from a shared types file if available
interface Post {
  date: string;
  brand: string;
  platform: string;
  sentiment: number;
  engagement: number;
  post: string;
  keywordMatches: string[];
  url: string;
}

interface TopPostsProps {
  // Update props: accept posts array and total directly
  posts: Post[] | null;
  total: number | null;
  loading: boolean; // Add loading state
  // Keep filter props if they are still needed for internal filtering
  // selectedBrands: string[];
  // selectedPlatforms: string[];
  // startDate?: string | null;
  // endDate?: string | null;
}

// Rows stay one line tall; the rest of the keywords are in the cell tooltip.
const MAX_KEYWORD_CHIPS = 2;

// Type for sort directions
type SortDirection = 'asc' | 'desc';

// Type for sort fields
type SortField = 'date' | 'engagement' | 'sentiment';

// Type for filter popover anchor
type FilterAnchor = HTMLElement | null;

// Type for filter columns
type FilterColumn = 'brand' | 'platform' | 'keywords';

const TopPosts: React.FC<TopPostsProps> = ({
  posts,
  total,
  loading
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  // Add state for sorting
  const [sortField, setSortField] = useState<SortField>('engagement');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  // State for filter popover
  const [filterAnchor, setFilterAnchor] = useState<Record<FilterColumn, FilterAnchor>>({
    brand: null,
    platform: null,
    keywords: null
  });
  
  // State for filter values
  const [brandFilters, setBrandFilters] = useState<string[]>([]);
  const [platformFilters, setPlatformFilters] = useState<string[]>([]);
  const [keywordFilters, setKeywordFilters] = useState<string[]>([]);

  // Platform name mapping
  const platformNameMapping: Record<string, string> = {
    'facebook': 'Facebook',
    'instagram': 'Instagram',
    'linkedin': 'LinkedIn',
    'tiktok': 'TikTok',
    'youtube': 'YouTube',
    'twitter': 'Twitter',
    'x': 'X'
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Number formatter helper
  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('en-US').format(Math.round(num));
  };

  // Handle sort request for a column
  const handleRequestSort = (field: SortField) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortField(field);
    // Reset to first page when sort changes
    setPage(0);
  };

  // Helper function to get proper platform name
  const getPlatformName = (platform: string): string => {
    return platformNameMapping[platform.toLowerCase()] || platform;
  };
  
  // Handle opening filter popover
  const handleFilterClick = (column: FilterColumn, event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchor({ ...filterAnchor, [column]: event.currentTarget });
  };
  
  // Handle closing filter popover
  const handleFilterClose = (column: FilterColumn) => {
    setFilterAnchor({ ...filterAnchor, [column]: null });
  };
  
  // Handle filter change
  const handleFilterChange = (column: FilterColumn, value: string) => {
    switch (column) {
      case 'brand':
        setBrandFilters(prev => 
          prev.includes(value) 
            ? prev.filter(item => item !== value) 
            : [...prev, value]
        );
        break;
      case 'platform':
        setPlatformFilters(prev => 
          prev.includes(value) 
            ? prev.filter(item => item !== value) 
            : [...prev, value]
        );
        break;
      case 'keywords':
        setKeywordFilters(prev => 
          prev.includes(value) 
            ? prev.filter(item => item !== value) 
            : [...prev, value]
        );
        break;
    }
    setPage(0); // Reset to first page when filter changes
  };
  
  // Clear filters for a specific column
  const clearFilters = (column: FilterColumn) => {
    switch (column) {
      case 'brand':
        setBrandFilters([]);
        break;
      case 'platform':
        setPlatformFilters([]);
        break;
      case 'keywords':
        setKeywordFilters([]);
        break;
    }
    setPage(0);
  };

  const header = (
    <SectionHeader
      title="Top Posts by Engagement"
      info="Top Posts displays the highest-engagement content sorted by engagement score. Use the column filters to narrow results by brand, platform, or keywords. Sort by date, sentiment, or engagement. Open a post to view the original content."
    />
  );

  // Show loading indicator if loading is true
  if (loading) {
    return (
      <SectionCard>
        {header}
        <SectionLoading label="Loading posts..." height={240} />
      </SectionCard>
    );
  }
  
  // Handle case where posts array is null or empty after loading
  if (!posts || posts.length === 0) {
    return (
      <SectionCard>
        {header}
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 5 }}>
          No posts match these filters. Widen the date range or include more brands or platforms.
        </Typography>
      </SectionCard>
    );
  }

  // Extract unique brands, platforms, and keywords for filters directly from the posts prop
  const uniqueBrands = Array.from(new Set(posts.map(post => post.brand)));
  const uniquePlatforms = Array.from(new Set(posts.map(post => getPlatformName(post.platform))));
  const uniqueKeywords = Array.from(new Set(
    posts.flatMap(post => 
      post.keywordMatches.filter((keyword, index, self) => self.indexOf(keyword) === index)
    )
  ));

  // Calculate counts for each filter option
  const brandCounts: Record<string, number> = {};
  const platformCounts: Record<string, number> = {};
  const keywordCounts: Record<string, number> = {};
  
  // Count brands and platforms using the posts prop
  posts.forEach((post: Post) => {
    const brand = post.brand;
    const platform = getPlatformName(post.platform);
    
    brandCounts[brand] = (brandCounts[brand] || 0) + 1;
    platformCounts[platform] = (platformCounts[platform] || 0) + 1;
    
    // For keywords, count each unique keyword per post once
    const uniquePostKeywords = post.keywordMatches.filter(
      (keyword, index, self) => self.indexOf(keyword) === index
    );
    
    uniquePostKeywords.forEach(keyword => {
      keywordCounts[keyword] = (keywordCounts[keyword] || 0) + 1;
    });
  });

  // Filter posts based ONLY on internal column filters (brand/platform/keywords)
  // External filters (selectedBrands, selectedPlatforms, dates) are now handled by the backend via useTopPostsData hook
  const filteredPosts = posts.filter(post => {
    const matchesBrandFilter = brandFilters.length === 0 || brandFilters.includes(post.brand);
    const matchesPlatformFilter = platformFilters.length === 0 || platformFilters.includes(getPlatformName(post.platform));
    
    const uniquePostKeywords = post.keywordMatches.filter((keyword, index, self) => self.indexOf(keyword) === index);
    const matchesKeywordFilter = keywordFilters.length === 0 || 
      uniquePostKeywords.some(keyword => keywordFilters.includes(keyword));
    
    // REMOVED external filter checks: matchesExternalBrand && matchesExternalPlatform &&
    return matchesBrandFilter && matchesPlatformFilter && matchesKeywordFilter;
  });

  // Sort the posts based on current sortField and sortDirection
  const sortedPosts = [...filteredPosts].sort((a, b) => {
    if (sortField === 'date') {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortDirection === 'asc' 
        ? dateA.getTime() - dateB.getTime() 
        : dateB.getTime() - dateA.getTime();
    } else if (sortField === 'engagement') {
      return sortDirection === 'asc' 
        ? a.engagement - b.engagement 
        : b.engagement - a.engagement;
    } else if (sortField === 'sentiment') {
      return sortDirection === 'asc' 
        ? a.sentiment - b.sentiment 
        : b.sentiment - a.sentiment;
    }
    return 0;
  });
  
  return (
    <SectionCard>
      {header}

      <TableContainer sx={{ mx: { xs: -2, md: -3 }, width: 'auto', px: { xs: 1, md: 1.5 } }}>
        <Table sx={{ '& .MuiTableCell-root': { px: 1.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'date'}
                  direction={sortField === 'date' ? sortDirection : 'asc'}
                  onClick={() => handleRequestSort('date')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Brand
                  <IconButton size="small" onClick={(e) => handleFilterClick('brand', e)}>
                    <FilterListIcon fontSize="small" color={brandFilters.length > 0 ? "primary" : "inherit"} />
                  </IconButton>
                </Box>
                <Popover
                  open={Boolean(filterAnchor.brand)}
                  anchorEl={filterAnchor.brand}
                  onClose={() => handleFilterClose('brand')}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    style: { maxHeight: 300, width: 250, overflow: 'auto' }
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Filter Brands</Typography>
                    <FormGroup>
                      {uniqueBrands.map(brand => (
                        <FormControlLabel
                          key={brand}
                          control={
                            <Checkbox
                              checked={brandFilters.includes(brand)}
                              onChange={() => handleFilterChange('brand', brand)}
                            />
                          }
                          label={`${brand} (${brandCounts[brand] || 0})`}
                        />
                      ))}
                    </FormGroup>
                    <Button 
                      sx={{ mt: 2 }}
                      size="small" 
                      onClick={() => clearFilters('brand')}
                      disabled={brandFilters.length === 0}
                    >
                      Clear Filters
                    </Button>
                  </Box>
                </Popover>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Platform
                  <IconButton size="small" onClick={(e) => handleFilterClick('platform', e)}>
                    <FilterListIcon fontSize="small" color={platformFilters.length > 0 ? "primary" : "inherit"} />
                  </IconButton>
                </Box>
                <Popover
                  open={Boolean(filterAnchor.platform)}
                  anchorEl={filterAnchor.platform}
                  onClose={() => handleFilterClose('platform')}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    style: { maxHeight: 300, width: 250, overflow: 'auto' }
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Filter Platforms</Typography>
                    <FormGroup>
                      {uniquePlatforms.map(platform => (
                        <FormControlLabel
                          key={platform}
                          control={
                            <Checkbox
                              checked={platformFilters.includes(platform)}
                              onChange={() => handleFilterChange('platform', platform)}
                            />
                          }
                          label={`${platform} (${platformCounts[platform] || 0})`}
                        />
                      ))}
                    </FormGroup>
                    <Button 
                      sx={{ mt: 2 }}
                      size="small" 
                      onClick={() => clearFilters('platform')}
                      disabled={platformFilters.length === 0}
                    >
                      Clear Filters
                    </Button>
                  </Box>
                </Popover>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'sentiment'}
                  direction={sortField === 'sentiment' ? sortDirection : 'asc'}
                  onClick={() => handleRequestSort('sentiment')}
                >
                  Sentiment
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'engagement'}
                  direction={sortField === 'engagement' ? sortDirection : 'asc'}
                  onClick={() => handleRequestSort('engagement')}
                >
                  Engagement
                </TableSortLabel>
              </TableCell>
              <TableCell>Post</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Keywords
                  <IconButton size="small" onClick={(e) => handleFilterClick('keywords', e)}>
                    <FilterListIcon fontSize="small" color={keywordFilters.length > 0 ? "primary" : "inherit"} />
                  </IconButton>
                </Box>
                <Popover
                  open={Boolean(filterAnchor.keywords)}
                  anchorEl={filterAnchor.keywords}
                  onClose={() => handleFilterClose('keywords')}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    style: { maxHeight: 300, width: 250, overflow: 'auto' }
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Filter Keywords</Typography>
                    <FormGroup>
                      {uniqueKeywords.map(keyword => (
                        <FormControlLabel
                          key={keyword}
                          control={
                            <Checkbox
                              checked={keywordFilters.includes(keyword)}
                              onChange={() => handleFilterChange('keywords', keyword)}
                            />
                          }
                          label={`${keyword} (${keywordCounts[keyword] || 0})`}
                        />
                      ))}
                    </FormGroup>
                    <Button 
                      sx={{ mt: 2 }}
                      size="small" 
                      onClick={() => clearFilters('keywords')}
                      disabled={keywordFilters.length === 0}
                    >
                      Clear Filters
                    </Button>
                  </Box>
                </Popover>
              </TableCell>
              <TableCell>Link</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPosts
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((post, index) => (
                <TableRow key={index} hover>
                  <TableCell sx={{
                    whiteSpace: 'nowrap',
                    minWidth: '100px',
                    color: 'text.secondary'
                  }}>
                    {post.date}
                  </TableCell>
                  <TableCell sx={{ 
                    maxWidth: '150px',
                    lineHeight: '1.2em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'middle',
                    height: '48px'
                  }}>
                    {post.brand}
                  </TableCell>
                  <TableCell>{getPlatformName(post.platform)}</TableCell>
                  <TableCell>
                    <SentimentBadge value={post.sentiment} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{formatNumber(post.engagement)}</TableCell>
                  <TableCell sx={{ maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle' }}>
                    <Tooltip 
                      title={post.post} 
                      arrow 
                      placement="top-start"
                      PopperProps={{
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, 8],
                            },
                          },
                          {
                            name: 'preventOverflow',
                            options: {
                              altAxis: true,
                              tether: false,
                              padding: 8
                            },
                          },
                        ],
                      }}
                    >
                      <span>{post.post}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ 
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'middle',
                    height: '48px',
                    lineHeight: '1.2em',
                    padding: '8px 16px'
                  }}>
                    <Tooltip 
                      title={post.keywordMatches
                        .filter((keyword, index, self) => self.indexOf(keyword) === index)
                        .join(', ')} 
                      arrow 
                      placement="top-start"
                    >
                      <Stack 
                        direction="row" 
                        flexWrap="nowrap" 
                        gap={0.5} 
                        sx={{ maxWidth: '100%', overflow: 'hidden' }}
                      >
                        {post.keywordMatches
                          .filter((keyword, index, self) => self.indexOf(keyword) === index)
                          .slice(0, MAX_KEYWORD_CHIPS)
                          .map((keyword, i) => (
                            <Chip
                              key={i}
                              label={keyword}
                              size="small"
                              sx={{
                                bgcolor: 'action.hover',
                                fontSize: '0.75rem',
                                height: '22px',
                                maxWidth: 110,
                                flexShrink: 1,
                                minWidth: 0,
                                '& .MuiChip-label': {
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }
                              }}
                            />
                          ))}
                        {(() => {
                          const extra = new Set(post.keywordMatches).size - MAX_KEYWORD_CHIPS;
                          return extra > 0 ? (
                            <Chip label={`+${extra}`} size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: '22px' }} />
                          ) : null;
                        })()}
                      </Stack>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Link
                      href={post.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}
                    >
                      View <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </Link>
                  </TableCell>
                </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        component="div"
        count={filteredPosts.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10]}
      />
    </SectionCard>
  );
};

export default TopPosts; 